import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { API_ENDPOINTS } from '@/config/api';
import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { ModuleDetailResponse } from '../types';
import { GetRecommendedQuizzes } from '../types/get-recommended-quizzes';
import { Assessment, ModuleSummary, PendingTasks } from '../types/modules-list';

export const getModuleDetail = (
  moduleId: string,
  moduleStatus: string
): Promise<ModuleDetailResponse> => {
  return axios.get(
    `${API_ENDPOINTS.ASSESSMENTS.MODULE_DETAIL}/${moduleId}/${moduleStatus}`
  );
};

// Mock data generators
const generateAssessment = (
  overrides: Partial<Assessment> = {}
): Assessment => {
  const totalMarks = faker.number.int({ min: 10, max: 300 });
  const marksObtained = faker.number.int({ min: 0, max: totalMarks });

  return {
    assessmentId: faker.string.uuid(),
    accuracy: faker.number.int({ min: 0, max: 100 }),
    numberOfQuestions: faker.number.int({ min: 10, max: 100 }),
    assessmentType: faker.helpers.arrayElement([
      'WORKSHEET',
      'QUIZ',
      'TEST',
      'PRACTICE',
    ]),
    assessmentName: faker.lorem.words(3),
    startDate: faker.date.recent({ days: 30 }).toISOString(),
    description: faker.lorem.sentence(),
    duration: faker.number.int({ min: 10, max: 120 }),
    publishStatus: faker.datatype.boolean(),
    paperName: faker.lorem.words(4),
    classID: faker.helpers.arrayElement(['7-8', '5-6', '3-4', '1-2']),
    completionStatus: faker.datatype.boolean(),
    assessmentCategoryType: faker.helpers.arrayElement(['TOPIC', 'MODULE']),
    assessmentCategoryId: faker.string.uuid(),
    assessmentCategoryName: faker.lorem.words(2),
    assessmentCategoryViewStatus: faker.helpers.arrayElement(['PRE', 'POST']),
    canGoback: faker.datatype.boolean(),
    timeCategory: faker.helpers.arrayElement(['TIMER', 'STOPWATCH']),
    reattemptAllowed: faker.datatype.boolean(),
    attemptStatus: faker.datatype.boolean(),
    solutionView: faker.datatype.boolean(),
    hintView: faker.datatype.boolean(),
    assessmentImageUrl: faker.image.url(),
    solutionSeen: faker.datatype.boolean(),
    lastAssessmentStatus: faker.helpers.arrayElement([
      'NOT STARTED',
      'IN PROGRESS',
      'COMPLETED',
    ]),
    noOfAttempt: faker.number.int({ min: 0, max: 5 }),
    allowTakingAssessment: faker.datatype.boolean(),
    difficultyLevel: faker.helpers.arrayElement(['Easy', 'Medium', 'Hard']),
    totalMarks,
    marksObtained,
    totalCoins: faker.number.int({ min: 20, max: 600 }),
    coinsEarned: faker.datatype.boolean()
      ? faker.number.int({ min: 0, max: 500 })
      : null,
    isLocked: faker.datatype.boolean(),
    instruction: faker.lorem.paragraph(),
    displayName: faker.lorem.words(3),
    lastTeachDate: faker.date.recent({ days: 10 }).getTime().toString(), // Epoch timestamp
    questionSheetUrl: faker.helpers.maybe(() => faker.internet.url(), {
      probability: 0.7,
    }),
    ...overrides,
  };
};

export const generateQuiz = (
  overrides: Partial<GetRecommendedQuizzes.ResponseBody> = {}
): GetRecommendedQuizzes.ResponseBody => ({
  preDefinedRoomId: faker.string.alphanumeric(6).toUpperCase(),
  roomName: faker.lorem.words(4),
  roomType: 'PRE-DEFINED-USER-QUIZ',
  numberOfQuestions: faker.number.int({ min: 5, max: 30 }),
  duration: faker.number.int({ min: 10, max: 60 }),
  banner: faker.image.url(),
  moduleId: faker.string.uuid(),
  attemptStatus: faker.datatype.boolean(),
  reattemptAllowed: faker.datatype.boolean(),
  lastMarksObtained: faker.number.int({ min: 0, max: 100 }),
  lastCoinsEarned: faker.number.int({ min: 10, max: 200 }),
  ...overrides,
});

const generateModuleSummary = (
  overrides: Partial<ModuleSummary> = {}
): ModuleSummary => ({
  totalAssessments: faker.number.int({ min: 5, max: 20 }),
  completedAssessments: faker.number.int({ min: 0, max: 15 }),
  percentile: faker.number.int({ min: 0, max: 100 }),
  moduleRanking: faker.number.int({ min: 1, max: 10_000 }),
  coinsEarned: faker.number.int({ min: 0, max: 1000 }),
  accuracy: Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, () =>
    faker.number.int({ min: 0, max: 100 })
  ),
  proficiency: Array.from(
    { length: faker.number.int({ min: 1, max: 5 }) },
    () => faker.number.float({ min: 0, max: 100, fractionDigits: 1 })
  ),
  speed: Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, () =>
    faker.number.float({ min: 0, max: 100, fractionDigits: 1 })
  ),
  ...overrides,
});

const generatePendingTasks = (
  overrides: Partial<PendingTasks> = {}
): PendingTasks => {
  const assessmentsCount = faker.number.int({ min: 0, max: 8 });
  return {
    count: assessmentsCount,
    pendingAssessments: Array.from({ length: assessmentsCount }, () =>
      generateAssessment({ completionStatus: false })
    ),
    ...overrides,
  };
};

export const getModuleDetailMockHandler = http.get(
  `${env.API_URL}${API_ENDPOINTS.ASSESSMENTS.MODULE_DETAIL}/:moduleId/:moduleStatus`,
  async (): Promise<HttpResponse<ModuleDetailResponse>> => {
    await networkDelay();

    const summary = generateModuleSummary();
    const pendingTasks = generatePendingTasks();
    const quizzesCount = faker.number.int({ min: 3, max: 8 });
    const topicAssessmentsCount = faker.number.int({ min: 5, max: 12 });
    const moduleAssessmentsCount = faker.number.int({ min: 1, max: 3 });

    // Guarantee at least one attempted quiz so the "View Report" entry point
    // (which opens the quiz summary modal) is always available in dev.
    const attemptedQuiz = generateQuiz({
      attemptStatus: true,
      lastMarksObtained: 80,
    });

    const mockResponse: ModuleDetailResponse = {
      status: 'success',
      responseBody: {
        summary,
        pendingTasks,
        quizzes: [
          attemptedQuiz,
          ...Array.from({ length: quizzesCount }, () => generateQuiz()),
        ],
        topicLevelAssessments: Array.from(
          { length: topicAssessmentsCount },
          () => generateAssessment({ assessmentCategoryType: 'TOPIC' })
        ),
        moduleLevelAssessments: Array.from(
          { length: moduleAssessmentsCount },
          () => generateAssessment({ assessmentCategoryType: 'MODULE' })
        ),
      },
    };

    return HttpResponse.json(mockResponse);
  }
);
